import React, { createContext, useContext, useEffect, useState, useCallback, useRef } from 'react';
import { apiGet, apiPut, apiDelete } from '../api/client';

export interface NotificationItem {
  id: number;
  type: string;
  category: string;
  title: string;
  body: string;
  priority: 'low' | 'normal' | 'high' | 'urgent';
  actionUrl?: string | null;
  metadata?: any;
  read: boolean;
  archived: boolean;
  createdAt: string;
}

export interface ToastItem {
  id: string;
  title: string;
  body: string;
  category: string;
  priority: string;
  actionUrl?: string | null;
}

interface NotificationContextType {
  notifications: NotificationItem[];
  unreadCount: number;
  loading: boolean;
  activeCategory: string;
  setActiveCategory: (cat: string) => void;
  fetchNotifications: (cat?: string) => Promise<void>;
  markAsRead: (id: number) => Promise<void>;
  markAllAsRead: () => Promise<void>;
  clearAll: () => Promise<void>;
  toasts: ToastItem[];
  dismissToast: (id: string) => void;
}

const NotificationContext = createContext<NotificationContextType | undefined>(undefined);

export function NotificationProvider({ children }: { children: React.ReactNode }) {
  const [notifications, setNotifications] = useState<NotificationItem[]>([]);
  const [unreadCount, setUnreadCount] = useState<number>(0);
  const [loading, setLoading] = useState<boolean>(false);
  const [activeCategory, setActiveCategory] = useState<string>('all');
  const [toasts, setToasts] = useState<ToastItem[]>([]);
  const eventSourceRef = useRef<EventSource | null>(null);

  const fetchNotifications = useCallback(async (cat?: string) => {
    const targetCat = cat ?? activeCategory;
    setLoading(true);
    try {
      const queryParams = new URLSearchParams();
      if (targetCat && targetCat !== 'all' && targetCat !== 'unread') {
        queryParams.set('category', targetCat);
      }
      if (targetCat === 'unread') {
        queryParams.set('unread', 'true');
      }
      const res = await apiGet<{ items: NotificationItem[]; unreadCount: number }>(
        `/api/notifications?${queryParams.toString()}`
      );
      setNotifications(res.items || []);
      setUnreadCount(res.unreadCount || 0);
    } catch (err) {
      // Graceful fallback
    } finally {
      setLoading(false);
    }
  }, [activeCategory]);

  const addToast = useCallback((item: { title: string; body: string; category?: string; priority?: string; actionUrl?: string | null }) => {
    const toastId = `${Date.now()}-${Math.random().toString(36).substr(2, 5)}`;
    const newToast: ToastItem = {
      id: toastId,
      title: item.title,
      body: item.body,
      category: item.category || 'system',
      priority: item.priority || 'normal',
      actionUrl: item.actionUrl,
    };

    setToasts((prev) => [newToast, ...prev].slice(0, 4));

    // Auto dismiss after 5 seconds
    setTimeout(() => {
      dismissToast(toastId);
    }, 5000);
  }, []);

  const dismissToast = useCallback((id: string) => {
    setToasts((prev) => prev.filter((t) => t.id !== id));
  }, []);

  const markAsRead = async (id: number) => {
    try {
      await apiPut(`/api/notifications/${id}/read`, {});
      setNotifications((prev) =>
        prev.map((n) => (n.id === id ? { ...n, read: true } : n))
      );
      setUnreadCount((prev) => Math.max(0, prev - 1));
    } catch (err) {
      // Fallback
    }
  };

  const markAllAsRead = async () => {
    try {
      await apiPut('/api/notifications/read-all', {});
      setNotifications((prev) => prev.map((n) => ({ ...n, read: true })));
      setUnreadCount(0);
    } catch (err) {
      // Fallback
    }
  };

  const clearAll = async () => {
    try {
      await apiDelete('/api/notifications/clear-all');
      setNotifications([]);
      setUnreadCount(0);
    } catch (err) {
      // Fallback
    }
  };

  // Setup SSE stream + 30s Polling fallback
  useEffect(() => {
    fetchNotifications();

    const token = localStorage.getItem('token');
    if (token && window.EventSource) {
      try {
        // Close existing
        if (eventSourceRef.current) {
          eventSourceRef.current.close();
        }

        const streamUrl = `/api/notifications/stream?token=${encodeURIComponent(token)}`;
        const es = new EventSource(streamUrl);

        es.addEventListener('notification', (e: any) => {
          try {
            const data = JSON.parse(e.data) as NotificationItem;
            setNotifications((prev) => [data, ...prev.filter((n) => n.id !== data.id)]);
            setUnreadCount((prev) => prev + 1);

            // Add live toast alert
            addToast({
              title: data.title,
              body: data.body,
              category: data.category,
              priority: data.priority,
              actionUrl: data.actionUrl,
            });
          } catch (err) {
            // Ignored
          }
        });

        eventSourceRef.current = es;
      } catch (err) {
        // EventSource fallback to polling
      }
    }

    // Polling fallback every 30s
    const pollInterval = setInterval(() => {
      fetchNotifications();
    }, 30000);

    return () => {
      clearInterval(pollInterval);
      if (eventSourceRef.current) {
        eventSourceRef.current.close();
      }
    };
  }, [fetchNotifications, addToast]);

  return (
    <NotificationContext.Provider
      value={{
        notifications,
        unreadCount,
        loading,
        activeCategory,
        setActiveCategory: (cat) => {
          setActiveCategory(cat);
          fetchNotifications(cat);
        },
        fetchNotifications,
        markAsRead,
        markAllAsRead,
        clearAll,
        toasts,
        dismissToast,
      }}
    >
      {children}
    </NotificationContext.Provider>
  );
}

export function useNotifications() {
  const ctx = useContext(NotificationContext);
  if (!ctx) {
    throw new Error('useNotifications must be used within a NotificationProvider');
  }
  return ctx;
}
